import { Dispatch } from 'react';

import { Stack, Typography } from '@mui/material';

import { PostDetailsProviderType } from '@/contexts/PostDetailsContext';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Posts } from '@/models/posts';
import { Violations } from '@/models/violations';

import Card from '../card';
import CreatorDetails from '../creatorDetails';
import MediaDisplay from '../mediaDisplay';

interface EvaluationProps {
  showIdentificationMarkers: PostDetailsProviderType['showIdentificationMarkers'];
  markerItems: PostDetailsProviderType['markerItems'];
  videoPlaybackStart: PostDetailsProviderType['videoPlaybackStart'];
  videoLastClick: PostDetailsProviderType['videoLastClick'];
  darkenImageStyle: PostDetailsProviderType['darkenImageStyle'];
  setVideoPlayedSeconds: PostDetailsProviderType['setVideoPlayedSeconds'];
  resetMarkers: PostDetailsProviderType['resetMarkers'];
}

interface PostProps {
  postData: Posts.Details | Violations.Details;
  activeIndex: number;
  setActiveIndex: Dispatch<React.SetStateAction<number>>;
  evaluationProps?: EvaluationProps;
}

interface PostCreatorProps {
  postData: Posts.Details | Violations.Details;
}

const PostCreator = ({ postData }: PostCreatorProps) => {
  const navigate = useWorkspaceNavigate();

  if (!postData)
    return <Typography variant="body2">Data not available</Typography>;

  return (
    <Card
      shadow
      content={
        <CreatorDetails
          id="detail-creator"
          data={postData}
          navigate={navigate}
        />
      }
    />
  );
};

const PostMedia = ({
  postData,
  activeIndex,
  setActiveIndex,
  evaluationProps
}: PostProps) => (
  <Card
    shadow
    sidePadding={false}
    style={{
      borderTop: '1px solid #cdcdcd',
      padding: '6px 0',
      height: 'calc(100% - 72px)'
    }}
    content={
      <MediaDisplay
        data={postData}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        showIdentificationMarkers={evaluationProps?.showIdentificationMarkers}
        markerItems={evaluationProps?.markerItems}
        videoPlaybackStart={evaluationProps?.videoPlaybackStart}
        videoLastClick={evaluationProps?.videoLastClick}
        filter={evaluationProps?.darkenImageStyle}
        onVideoProgress={evaluationProps?.setVideoPlayedSeconds}
        onMarkersReset={evaluationProps?.resetMarkers}
      />
    }
  />
);

const Post = ({
  postData,
  activeIndex,
  setActiveIndex,
  evaluationProps
}: PostProps) => (
  <Stack height="100%">
    <PostCreator postData={postData} />
    <PostMedia
      postData={postData}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      evaluationProps={evaluationProps}
    />
  </Stack>
);

export default Post;
